import { each, isEmpty, mapValues, set } from "lodash";
import validate from "validate.js";
import listingValidationNonRequired from "../../listing/ducks/listingValidationNonRequired";
import listingValidation from "../../listing/ducks/validation";
import { validateFieldWithKey } from "../../utils/findAndValidateField";
import { constraints } from "../../utils/validation";

export const compConstraintsNonRequired = {
    Industrial: {
        "listing.attributes.largestContigSf": {
            ...constraints.number,
        },
        "listing.attributes.officeSf": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.yardSf": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.officeBreakdownSfRate": {
            ...constraints.number,
        },
        "listing.attributes.warehouseShellBreakdownRate": {
            ...constraints.number,
        },
        "listing.attributes.blendedRate": {
            ...constraints.number,
        },
        "listing.attributes.ampsNum": {
            ...constraints.number,
        },
        "listing.attributes.phaseNum": {
            ...constraints.number,
        },
        "listing.attributes.gradeLevelDoorsNum": {
            ...constraints.integer,
        },
        "listing.attributes.dockHighDoorsNum": {
            ...constraints.integer,
        },
        "listing.attributes.railDoorsNum": {
            ...constraints.integer,
        },
        "listing.attributes.clearHeightMin": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.clearHeightMax": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.cranesQty": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.industrialSf": {
            ...constraints.wholeNumber,
        },
    },
    Hospitality: {
        "listing.attributes.hospitalitySf": {
            ...constraints.id,
        },
        "listing.attributes.hospitalityUnitCount": {
            ...constraints.id,
        },
        "listing.attributes.roomCount": {
            ...constraints.id,
        },
    },
    Educational: {
        "listing.attributes.classroomCount": {
            ...constraints.number,
        },
        "listing.attributes.auditoriumSeatsCount": {
            ...constraints.number,
        },
    },
    totalConsideration: {
        commencementDate: {
            ...constraints.date,
        },
        expirationDate: {
            ...constraints.date,
        },
        termNumMonths: {
            ...constraints.number,
        },
        squareFeet: {
            ...constraints.number,
        },
        escalationRate: {
            ...constraints.number,
        },
        escalationTerm: {
            ...constraints.number,
        },
        totalConsiderationAmount: {
            ...constraints.number,
        },
    },
    annualRentCalculationConstraints: {
        ratePerYear: {
            ...constraints.number,
        },
    },
    montlyRentCalculationConstraints: {
        ratePerSqftPerMonth: {
            ...constraints.number,
        },
        ratePerSqftPerYear: {
            ...constraints.number,
        },
        ratePerMonth: {
            ...constraints.number,
        },
    },
    listingInformation: {
        "listing.dateListed": {
            ...constraints.date,
        },
        "listing.dateAvailable": {
            ...constraints.date,
        },
        "listing.expirationDate": {
            ...constraints.date,
        },
        "listing.leaseExpirationDate": {
            ...constraints.date,
        },
        "listing.attributes.numBldg": {
            ...constraints.number,
        },
        "listing.attributes.lotAcres": {
            ...constraints.number,
        },
        "listing.attributes.lotSf": {
            ...constraints.number,
        },
        "listing.sfAvail": {
            ...constraints.number,
        },
        "listing.sfDivisibleTo": {
            ...constraints.number,
        },
        "listing.sellerLandlordCommonCompanyName": {
            ...constraints.maxLength,
        },
        "listing.sellerLandlordLegalCompanyName": {
            ...constraints.maxLength,
        },
    },
    leaseDetails: {
        tiRate: {
            ...constraints.number,
            ...constraints.between0And1000,
        },
        movingAllowanceAmount: {
            ...constraints.number,
        },
        naicsCode: {
            ...constraints.id,
        },
        sicCode: {
            ...constraints.id,
        },
        currentSf: {
            ...constraints.number,
        },
        squareFeetChange: {
            ...constraints.number,
        },
    },
    Lease: {
        //Comp information
        transactionDate: {
            ...constraints.date,
        },
        totalKmCommission: {
            ...constraints.number,
        },
        outsideBrokerCommission: {
            ...constraints.number,
        },
    },
    Sale: {
        //required fields
        transactionDate: {
            ...constraints.date,
        },
        totalKmCommission: {
            ...constraints.number,
        },
        totalConsiderationAmount: {
            ...constraints.number,
        },
        squareFeet: {
            ...constraints.number,
        },
        closedDate: {
            ...constraints.date,
        },
    },
    parkingInformation: {
        "listing.attributes.parkingTotal": {
            ...constraints.number,
        },
        "listing.attributes.surfaceParkingSpaceCount": {
            ...constraints.number,
        },
        "listing.attributes.parkingGarageSpaceCount": {
            ...constraints.number,
        },
        "listing.attributes.parkingReservedSpaceCount": {
            ...constraints.number,
        },
        "listing.attributes.carportParkingSpaceCount": {
            ...constraints.number,
        },
    },
    listingFinancial: {
        "listing.improvementAllowance": {
            ...constraints.number,
        },
        "listing.askingRentMax": {
            ...constraints.number,
        },
        "listing.askingRentPerMonth": {
            ...constraints.number,
        },
        "listing.baseRentPerYear": {
            ...constraints.number,
        },
        "listing.nnnPerMonth": {
            ...constraints.number,
        },
        "listing.nnnPerYear": {
            ...constraints.number,
        },
        "listing.totalMonthlyRent": {
            ...constraints.number,
        },
        "listing.totalRentPerYear": {
            ...constraints.number,
        },
    },
};

export const installmentConstraints = {
    amount: {
        ...constraints.number,
    },
    dueDate: {
        ...constraints.date,
    },
};

const validateNonRequired = (comp, useType, transactionType, documents) => {
    let whatToValidate = [];
    switch (transactionType) {
        case "Lease":
            whatToValidate = [
                useType,
                transactionType,
                "leaseDetails",
                "totalConsideration",
                "listingInformation",
                "buildingInformation",
                "parkingInformation",
                "listingFinancial",
            ];
            break;
        case "Sale":
            whatToValidate = [
                useType,
                transactionType,
                "listingInformation",
                "buildingInformation",
                "parkingInformation",
                "saleFinancial",
            ];
            break;
        default:
            break;
    }
    let combinedConstraints = {};
    each(whatToValidate, (section) => {
        combinedConstraints = { ...combinedConstraints, ...compConstraintsNonRequired[section] };
    });
    let errors = {
        ...mapValues(validate(comp, combinedConstraints, { fullMessages: false }), (error) => {
            return error[0];
        }),
        ...listingValidation.validateListingBrokersCompany(comp?.listing?.listingBrokers),
        ...listingValidationNonRequired.validateMixedUses(comp?.listing?.attributes?.mixedUses),
        ...listingValidationNonRequired.validatePortfolioProperties(comp?.listing?.attributes?.portfolioProperties),
        ...listingValidationNonRequired.validateCondos(comp?.listing?.attributes?.condos),
        ...validateInstallments(comp.installments),
    };

    return errors;
};

const validateInstallments = (installments) => {
    let errors = {};
    if (installments) {
        for (let i = 0; i < installments.length; i++) {
            const validationResponse = validateInstallment(installments[i]);
            if (validationResponse) {
                set(errors, [installments[i].uniqueId], {
                    ...mapValues(validationResponse, (value) => value[0]),
                });
            }
        }
    }
    return isEmpty(errors) ? {} : { installments: errors };
};

const validateInstallment = (installment) => {
    return validate(installment, installmentConstraints, { fullMessages: false });
};

const validateCompByField = (section, id, value, comp) => {
    const sectionsRequiringFullValidation = [
        "listingFinancial",
        "montlyRentCalculationConstraints",
        "annualRentCalculationConstraints",
        "totalConsideration",
    ];
    if (sectionsRequiringFullValidation.includes(section)) {
        const sectionConstraints = compConstraintsNonRequired[section];
        const sectionErrors = validate(comp, sectionConstraints, { fullMessages: false }) || {};
        return Object.keys(sectionConstraints).map((fullKey) => ({
            fullKey,
            resolved: isEmpty(sectionErrors[fullKey]),
        }));
    } else if (section === "installment") {
        const field = id.split(".").pop();
        const { result } = validateFieldWithKey(installmentConstraints, field, value);
        return [{ fullKey: id, resolved: isEmpty(result) }];
    }

    const { fullKey, result } = validateFieldWithKey(compConstraintsNonRequired, id, value, section);
    return [{ fullKey, resolved: !!fullKey && isEmpty(result) }];
};

export default {
    validateNonRequired,
    validateInstallments,
    validateCompByField,
};
